/** AgentEditorWidget — URL-driven tab shell for per-agent settings.
 *
 * Replaces the slot-based stub. Reads agentId from useParams, derives the
 * active tab from the current URL, and renders child routes via <Outlet />.
 *
 * Tabs: Config, Prompt, Owners, Experiment, Tools, Skills, plus a
 * Provenance tab shown only for Argus and only to admins (the backend
 * generation endpoint requires admin; the fleet manifest is meaningless
 * for any other agent).
 * All tabs require admin OR owner access — the same gate that controls
 * whether the agent appears in the settings sidebar.
 */

import { useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { useAgentCatalog } from '../../app/providers/AgentCatalogProvider';
import { useSettingsAccess } from '../../components/settings/access';

type AgentTab = 'config' | 'prompt' | 'owners' | 'experiment' | 'tools' | 'skills' | 'provenance';

const AGENT_TABS: ReadonlyArray<{ value: AgentTab; label: string }> = [
  { value: 'config', label: 'Config' },
  { value: 'prompt', label: 'Prompt' },
  { value: 'owners', label: 'Owners' },
  { value: 'experiment', label: 'Experiment' },
  { value: 'tools', label: 'Tools' },
  { value: 'skills', label: 'Skills' },
];

// Argus-only: the fleet provenance manifest is meaningless for any other agent.
const ARGUS_AGENT_ID = 'argus';

function extractTab(pathname: string): AgentTab {
  if (pathname.endsWith('/prompt')) return 'prompt';
  if (pathname.endsWith('/owners')) return 'owners';
  if (pathname.endsWith('/experiment')) return 'experiment';
  if (pathname.endsWith('/tools')) return 'tools';
  if (pathname.endsWith('/skills')) return 'skills';
  if (pathname.endsWith('/provenance')) return 'provenance';
  return 'config';
}

export function AgentEditorWidget() {
  const { agentId } = useParams<{ agentId: string }>();
  const { agents } = useAgentCatalog();
  const { canAccessAgent, isAdmin } = useSettingsAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedTab = useMemo(() => extractTab(location.pathname), [location.pathname]);

  const displayName = useMemo(
    () => (agentId ? (agents.find((a) => a.id === agentId)?.displayName ?? agentId) : null),
    [agents, agentId],
  );

  const tabs = useMemo(
    () =>
      agentId === ARGUS_AGENT_ID && isAdmin
        ? [...AGENT_TABS, { value: 'provenance' as const, label: 'Provenance' }]
        : AGENT_TABS,
    [agentId, isAdmin],
  );

  // All tabs — including Experiment and Tools — require admin OR owner access.
  // Same gate as the settings sidebar: canAccessAgent = is_admin || owned_agents.includes(agentId).
  const hasAccess = agentId != null && canAccessAgent(agentId);
  const isManifestManaged = agentId === ARGUS_AGENT_ID;

  const handleTabChange = (tab: string) => {
    if (!agentId) return;
    navigate(`/settings/agents/${agentId}/${tab}`);
  };

  return (
    <div className="flex grow flex-col gap-4 px-4 pt-4 pb-12">
      {agentId && displayName && (
        <header>
          <h2 id={`agent-editor-${agentId}-heading`} className="m-0 text-base font-semibold">
            {displayName}
          </h2>
          <p className="m-0 text-sm text-muted-foreground">Agent ID: {agentId}</p>
        </header>
      )}
      {/* Hard redirect if agent is in URL but caller lacks access */}
      {agentId && !hasAccess && <Navigate to="/settings/agents" replace />}

      {agentId && hasAccess && isManifestManaged && (
        <p className="m-0 rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          Argus is managed from its repository manifest and prompt. Settings changes are disabled.
        </p>
      )}

      {agentId && hasAccess && !isManifestManaged && (
        <Tabs value={selectedTab} onValueChange={handleTabChange}>
          <TabsList aria-label="Agent sections" variant="line" className="w-full gap-4 border-b border-border">
            {tabs.map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="rounded-none pb-2 data-active:font-semibold data-active:text-primary"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {hasAccess && !isManifestManaged && <Outlet />}
    </div>
  );
}
